// Media for student WTF submissions in the admin review screens.
// The API adds `mediaKind` ("audio" | "image" | "video"), a short-lived signed `mediaPreviewUrl`
// (the bucket is private, so stored URLs can't be opened directly) and `mediaUnavailable` for
// old uploads that saved a browser-only link. Files in the old storage bucket can't be signed and
// fail to load, which is shown as "not available" too.
import React, { useEffect, useState } from "react";
import { FileX, Video } from "lucide-react";

export const submissionMediaKind = (submission) => {
  if (!submission) return null;
  if (submission.mediaKind) return submission.mediaKind;
  if (submission.type === "voice") return "audio";
  const original = (submission.metadata?.originalType || "").toLowerCase();
  return ["image", "video"].includes(original) ? original : null;
};

const Unavailable = ({ compact, kind }) =>
  compact ? (
    <div className="w-14 h-14 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center" title="File not available">
      <FileX className="w-5 h-5 text-gray-400" />
    </div>
  ) : (
    <div className="bg-gray-50 border border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-600">
      <FileX className="w-10 h-10 mx-auto mb-2 text-gray-400" />
      <p className="font-medium">This {kind === "audio" ? "voice note" : kind || "file"} is not available</p>
      <p className="text-sm text-gray-500 mt-1">
        The file was not saved correctly when it was submitted, or it is stored in old storage that can no longer be reached.
      </p>
    </div>
  );

// Small square thumbnail for queue rows.
export function SubmissionThumb({ submission }) {
  const kind = submissionMediaKind(submission);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [submission?.mediaPreviewUrl]);
  if (!kind || kind === "audio") return null;
  const src = kind === "video" ? submission.mediaThumbnailUrl : submission.mediaPreviewUrl;
  if (submission.mediaUnavailable || (kind === "image" && (!src || failed))) return <Unavailable compact kind={kind} />;
  if (kind === "video" && (!src || failed)) {
    return (
      <div className="w-14 h-14 rounded-md bg-gray-900 flex items-center justify-center" title="Video">
        <Video className="w-6 h-6 text-white" />
      </div>
    );
  }
  return (
    <div className="relative w-14 h-14 flex-none">
      <img
        src={src}
        alt=""
        className="w-14 h-14 rounded-md object-cover border border-gray-200 bg-white"
        onError={() => setFailed(true)}
      />
      {kind === "video" && (
        <span className="absolute bottom-0.5 right-0.5 bg-black/70 rounded px-1">
          <Video className="w-3 h-3 text-white" />
        </span>
      )}
    </div>
  );
}

// Full-size preview for the review popup.
export function SubmissionMediaPreview({ submission }) {
  const kind = submissionMediaKind(submission);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [submission?.mediaPreviewUrl]);
  const src = submission?.mediaPreviewUrl;
  if (!kind) return null;
  if (submission.mediaUnavailable || !src || failed) return <Unavailable kind={kind} />;

  if (kind === "audio") {
    return (
      <audio className="w-full" controls src={src} onError={() => setFailed(true)}>
        Your browser does not support the audio element.
      </audio>
    );
  }
  if (kind === "video") {
    return (
      <video
        src={src}
        poster={submission.mediaThumbnailUrl || undefined}
        controls
        className="w-full max-h-[480px] rounded bg-black"
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <a href={src} target="_blank" rel="noreferrer" title="Open full size">
      <img
        src={src}
        alt={submission.title || "Student submission"}
        className="max-w-full max-h-[480px] object-contain rounded mx-auto bg-white"
        onError={() => setFailed(true)}
      />
    </a>
  );
}

export const submissionKindLabel = (submission) => {
  const kind = submissionMediaKind(submission);
  if (kind === "audio") return "Voice Note";
  if (kind === "image") return "Image";
  if (kind === "video") return "Video";
  return "Article";
};
